import { DeleteOutlined } from "@ant-design/icons";
import * as Yup from "yup";

import { Avatar, Button, Empty, Flex, Input, List, Modal } from "antd";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useCreateOnlineOrder2 } from "../../api/api.post";
import { useNotifications2 } from "../../components/notifications/notification";
import { CardPaymentForm } from "../../components/payment-forms/card-payment.form";
import { RadioGroupComponent } from "../../components/radio.group.component";
import { useStoreContext } from "../../providers/store.provider";
import {
  AUTHORIZATION_TEMPLATE_CONSENT_ID,
  ICardDetails,
  IProductForScanOrder,
  IProductOnline,
  ONLINE_ORDER_FULLFILMENT_TYPE,
  ONLINE_ORDER_TYPE,
  PAYMENT_TYPE,
  StoreDetailsPublicResponse,
} from "../../types/types";
import { toFixed, toFixedDisplay } from "../../utils/number.utils";
import {
  calculateItemTotal,
  calculateProductTotal,
  calculateTaxtotal,
} from "../../utils/product.utils";

export default function ReservationsCheckout({
  open,
  onCancel,
  onOK,
  storeDetails,
  items,
  onRemoveItems,
}: {
  storeDetails?: StoreDetailsPublicResponse;
  items: IProductOnline[];
  open: boolean;
  onOK: () => void;
  onCancel: () => void;
  onRemoveItems: (item: IProductOnline) => void;
}) {
  const { id } = useParams();
  const { currency } = useStoreContext();

  const grossTotal = useMemo(
    () => calculateProductTotal(items, storeDetails?.tax),
    [items, storeDetails],
  );

  const { mutateAsync, isPending } = useCreateOnlineOrder2(id);

  const { notifyError, notifySuccess, notification } = useNotifications2();

  const [itemsToCheckout, setItemsToCheckout] = useState<
    IProductForScanOrder[]
  >([]);

  useEffect(() => {
    if (items) {
      setItemsToCheckout(
        items.map((item) => {
          return {
            ...item,
            uniqueId: uuidv4(),
          };
        }),
      );
    }
  }, [items]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      phone: "",
      country: "USA",
      zipcode: "",
      address: "",
      paymentType: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter First Name"),
      lastName: Yup.string().required("Please enter Last Name"),
      phone: Yup.string().required("Please enter phone"),
      email: Yup.string().email("Invalid email format"),
      paymentType: Yup.string().required("Please select payment type"),
    }),
    onSubmit: async (values) => {
      if (values.paymentType === PAYMENT_TYPE.CARD_ONLINE) {
        if (!acceptTerms) {
          notifyError("Please accept terms");
          return;
        }

        if (!cardDetails) {
          notifyError("Please enter payment details");
          return;
        }

        if (!cardDetails.cardToken) {
          notifyError("Please enter payment info details");
          return;
        }

        if (!cardDetails.expiry) {
          notifyError("Please enter card expiry");
          return;
        }
      }

      mutateAsync({
        products: itemsToCheckout,
        amount: toFixed(itemsTotal),
        tax: toFixed(storeDetails?.tax),
        total: toFixed(grossTotal),
        paymentType: formik.values.paymentType as PAYMENT_TYPE,
        type: ONLINE_ORDER_TYPE.NONE, // this is plain online
        fullfilmentType: ONLINE_ORDER_FULLFILMENT_TYPE.DELIVERY,
        customer: {
          country: "USA",
          firstName: values.firstName,
          lastName: values.lastName,
          phone: `+1${values.phone}`,
          state: values.state,
          street1: values.street1,
          zipcode: values.zipcode,
          email: values.email,
        },
        authConsentTemplateId:
          AUTHORIZATION_TEMPLATE_CONSENT_ID.CUSTOMER_PAY_INVOICE_ONLINE_CARD,
        nonce: uuidv4(),
        zipCode: values.zipcode,
        cardDetails,
      })
        .then(() => {
          formik.resetForm();
          notifySuccess({
            title: "Success",
            description: "Order successfully placed.",
          });
          onOK();
        })
        .catch((err) => {
          if (err.status === 401) {
            notifyError("Login code expired. Please enter new one.");
          } else if (err.status === 403) {
            notifyError("Please enter new login.");
          } else {
            notifyError("Whoops! Something went wrong.");
          }
        });
    },
  });

  const itemsTotal = useMemo(() => {
    return calculateItemTotal(itemsToCheckout);
  }, [itemsToCheckout]);

  const taxTotal = useMemo(() => {
    if (storeDetails?.tax) {
      return calculateTaxtotal(itemsToCheckout, +storeDetails.tax);
    }
    return 0;
  }, [storeDetails?.tax, itemsToCheckout]);

  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);

  const [cardDetails, setCardDetails] = useState<ICardDetails>();

  return (
    <Modal
      title="Checkout"
      open={open}
      onOk={() => formik.submitForm()}
      onCancel={onCancel}
      footer={null}
    >
      {notification}
      <div className="divColumn">
        <div className="my-10">
          {itemsToCheckout.length === 0 && <Empty description="No Items" />}
          {itemsToCheckout.length > 0 && (
            <Flex vertical gap={20}>
              <List
                dataSource={itemsToCheckout}
                itemLayout="horizontal"
                bordered
                renderItem={(item, index) => (
                  <RowItem
                    item={item}
                    key={index}
                    onRemoveItems={(item) => onRemoveItems(item)}
                  />
                )}
              />

              <div className="my-4 flex flex-col">
                <div className="flex my-2 justify-between">
                  <span className="flex w-[100px] items-center">
                    Net Total:{" "}
                  </span>
                  <strong className="font-bold text-xl">
                    {currency}
                    {toFixedDisplay(itemsTotal)}
                  </strong>
                </div>

                <div className="flex my-2 justify-between">
                  <span className="flex w-[100px] items-center">
                    Tax: ({storeDetails?.tax ?? 0}%)
                  </span>
                  <strong className="font-bold text-xl">
                    {currency}
                    {toFixedDisplay(taxTotal)}
                  </strong>
                </div>

                <div className="flex my-2 justify-between">
                  <span className="flex w-[100px] items-center">
                    Delivery Fee:
                  </span>
                  <strong className="font-bold text-xl">{currency}2.00</strong>
                </div>

                <div className="flex my-2 justify-between">
                  <span className="flex w-[100px] items-center">Total:</span>
                  <strong className="font-bold text-xl">
                    {currency}
                    {toFixedDisplay(grossTotal + 2)}
                  </strong>
                </div>
              </div>

              <div>
                <Flex vertical gap={5}>
                  Enter Phone Number
                  <Flex>
                    <Input
                      placeholder="Phone number"
                      prefix="+1"
                      {...formik.getFieldProps("phone")}
                    />
                  </Flex>
                </Flex>

                {formik.values.phone.length >= 10 && (
                  <Flex vertical>
                    <Flex vertical gap={5} className="my-4">
                      First Name
                      <Input
                        placeholder="First Name"
                        {...formik.getFieldProps("firstName")}
                      />
                      {formik.touched.firstName && formik.errors.firstName && (
                        <div style={{ color: "red", fontSize: 12 }}>
                          {formik.errors.firstName}
                        </div>
                      )}
                    </Flex>

                    <Flex vertical gap={5} className="my-4">
                      Last Name
                      <Input
                        placeholder="Last Name"
                        {...formik.getFieldProps("lastName")}
                      />
                      {formik.touched.lastName && formik.errors.lastName && (
                        <div style={{ color: "red", fontSize: 12 }}>
                          {formik.errors.lastName}
                        </div>
                      )}
                    </Flex>

                    <Flex vertical gap={5} className="my-4">
                      Email
                      <Input
                        placeholder="Email"
                        {...formik.getFieldProps("email")}
                      />
                    </Flex>

                    {Object.keys(formik.errors).length > 0 && (
                      <Flex vertical gap={5}>
                        <span className="text-xs text-red-500">
                          {
                            (formik.errors as any)[
                              Object.keys(formik.errors)[0]
                            ]
                          }
                        </span>
                      </Flex>
                    )}

                    <div className="my-5 flex flex-col gap-5">
                      <RadioGroupComponent
                        defaultValue="card"
                        items={[
                          ...(storeDetails?.allowCard
                            ? [
                                {
                                  label: "Card",
                                  value: PAYMENT_TYPE.CARD_ONLINE,
                                },
                              ]
                            : []),
                          ...(storeDetails?.allowCash
                            ? [
                                {
                                  label: "Cash",
                                  value: PAYMENT_TYPE.CASH,
                                },
                              ]
                            : []),
                          ...(storeDetails?.allowApplePay
                            ? [
                                {
                                  label: "Apple Pay",
                                  value: PAYMENT_TYPE.APPLE_PAY,
                                },
                              ]
                            : []),
                        ]}
                        onChange={(value) => {
                          formik.setFieldValue("paymentType", value);
                        }}
                      />

                      {formik.values.paymentType ===
                        PAYMENT_TYPE.CARD_ONLINE && (
                        <div>
                          <CardPaymentForm
                            onSuccess={(cardDetails) =>
                              setCardDetails(cardDetails)
                            }
                            context="customer"
                            onChangeAcceptTerms={(value) =>
                              setAcceptTerms(value)
                            }
                            onChangeZipCode={(value) =>
                              formik.setFieldValue("zipcode", value)
                            }
                            onError={(message) => {}}
                          />
                        </div>
                      )}
                      <Button
                        type="primary"
                        className="w-full"
                        disabled={Object.keys(formik.errors).length > 0}
                        onClick={() => formik.submitForm()}
                        loading={isPending}
                      >
                        {"Place Order"}
                      </Button>
                    </div>
                  </Flex>
                )}
              </div>
            </Flex>
          )}
        </div>
      </div>
    </Modal>
  );
}

function RowItem({
  item,
  onRemoveItems,
}: {
  item: IProductForScanOrder;
  onRemoveItems: (item: IProductForScanOrder) => void;
}) {
  const { currency } = useStoreContext();

  return (
    <List.Item>
      <Flex vertical style={{ flex: 1, gap: 10 }}>
        <Flex style={{ flex: 1 }}>
          <List.Item.Meta
            avatar={<Avatar src={item.cover} />}
            title={<span>{item.title}</span>}
            description={
              <span>
                {currency}
                {toFixedDisplay(item.price?.value)}
              </span>
            }
          />
          <Flex gap={20} align="center">
            <DeleteOutlined
              style={{ color: "red", cursor: "pointer" }}
              onClick={() => onRemoveItems(item)}
            />
          </Flex>
        </Flex>
      </Flex>
    </List.Item>
  );
}
